import { useEffect, useState, useCallback } from "react";
import { CircularProgress, Divider } from "@mui/material";
import { FiSearch, FiPlus, FiInbox, FiSend, FiStar, FiTrash2, FiMail, FiX, FiAlertOctagon, FiMaximize2, FiMinimize2, FiEyeOff, FiEye } from "react-icons/fi";
import { MdLabelImportantOutline } from "react-icons/md";
import { apiRequest } from "../../../api/apiHelper";

const folders = [
  { label: "Inbox",     icon: <FiInbox />,                  value: "inbox"     },
  { label: "Sent",      icon: <FiSend />,                   value: "sent"      },
  { label: "Starred",   icon: <FiStar />,                   value: "starred"   },
  { label: "Important", icon: <MdLabelImportantOutline />,  value: "important" },
  { label: "Drafts",    icon: <FiMail />,                   value: "draft"     },
  { label: "Spam",      icon: <FiAlertOctagon />,           value: "spam"      },
  { label: "Trash",     icon: <FiTrash2 />,                 value: "trash"     },
];

const FOLDER_API_MAP = { inbox: "inbox", sent: "sent", draft: "draft", spam: "spam", trash: "trash", important: "important", starred: "STARRED" };

function extractName(raw = "") {
  const m = raw.match(/^([^<]+)</);
  if (m) return m[1].trim();
  const at = raw.indexOf("@");
  if (at > 0) return raw.substring(0, at);
  return raw || "Unknown";
}
function extractEmail(raw = "") {
  const m = raw.match(/<([^>]+)>/);
  return m ? m[1] : raw;
}
const COLORS = ["#1976d2","#388e3c","#f57c00","#7b1fa2","#c62828","#00838f","#558b2f","#6d4c41","#455a64","#ad1457"];
function avatarColor(s = "") {
  let h = 0; for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h);
  return COLORS[Math.abs(h) % COLORS.length];
}
function formatDate(d) {
  const date = new Date(d), now = new Date();
  if (date.toDateString() === now.toDateString())
    return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function SenderCard({ raw, onSendMail }) {
  const name = extractName(raw), email = extractEmail(raw), color = avatarColor(name);
  return (
    <div style={{ position:"absolute", zIndex:1000, top:"100%", left:0, marginTop:4, background:"#fff", borderRadius:12, boxShadow:"0 4px 20px rgba(0,0,0,0.18)", padding:"20px 20px 16px", minWidth:240, pointerEvents:"all" }} onClick={e=>e.stopPropagation()}>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
        <div style={{ width:48, height:48, borderRadius:"50%", background:color, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:20, fontWeight:700 }}>{name.charAt(0).toUpperCase()}</div>
        <div style={{ minWidth:0 }}>
          <div style={{ fontWeight:600, fontSize:14, color:"#202124", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{name}</div>
          <div style={{ fontSize:12, color:"#5f6368", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{email}</div>
        </div>
      </div>
      <button onClick={onSendMail} style={{ width:"100%", padding:"8px 0", borderRadius:20, border:"1px solid #dadce0", background:"#fff", cursor:"pointer", fontSize:13, fontWeight:500, color:"#1a73e8", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}
        onMouseEnter={e=>e.currentTarget.style.background="#f1f3f4"} onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
        <FiMail size={14}/> Send Mail
      </button>
    </div>
  );
}

// Compose window — supports normal + wide mode, CC/BCC
function ComposeWindow({ onClose, initialTo = "", onSent, onDrafted, onGmailDisconnected }) {
  const [to, setTo] = useState(initialTo);
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [wide, setWide] = useState(false);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleGmailError = (errorCode, errorMessage) => {
    if (errorCode === "GMAIL_DISCONNECTED") {
      console.log("🔴 Gmail token expired - global badge will show via socket");
      // Global badge + toast will show automatically via socket event
      // Just close the compose window
      onGmailDisconnected?.();
      return true;
    }
    return false;
  };

  const handleSend = async () => {
    if (!to || !subject) return;
    setSending(true);
    setError("");
    try {
      console.log("📧 Attempting to send email...");
      await apiRequest("post", "/gmail-url/send-email", { to, cc: cc || undefined, bcc: bcc || undefined, subject, message: body });
      console.log("✅ Email sent successfully!");
      onSent?.();
      onClose();
    } catch (e) {
      console.error("❌ Send failed with error:", e);
      console.log("📊 Error details:", {
        message: e.message,
        response_code: e.response_code,
        data: e.data,
        code: e.data?.code
      });
      
      // Check if it's a Gmail disconnection error
      if (e.response_code === "GMAIL_DISCONNECTED" || e.data?.code === "GMAIL_DISCONNECTED") {
        console.log("🔴 Gmail Disconnected Error Detected! Triggering parent handler");
        handleGmailError("GMAIL_DISCONNECTED", e.message);
        return;
      }

      // Show generic error
      setError(e.message || "Failed to send email. Please try again.");
    }
    finally { setSending(false); }
  };

  const handleSaveDraft = async () => {
    if (!to && !subject && !body) return;
    setSaving(true);
    setError("");
    try {
      console.log("💾 Attempting to save draft...");
      await apiRequest("post", "/gmail-url/create-draft-v1", { to, cc: cc || undefined, bcc: bcc || undefined, subject, message: body });
      console.log("✅ Draft saved successfully!");
      onDrafted?.();
      onClose();
    } catch (e) {
      console.error("❌ Save draft failed with error:", e);
      console.log("📊 Error details:", {
        message: e.message,
        response_code: e.response_code,
        data: e.data,
        code: e.data?.code
      });
      
      // Check if it's a Gmail disconnection error
      if (e.response_code === "GMAIL_DISCONNECTED" || e.data?.code === "GMAIL_DISCONNECTED") {
        console.log("🔴 Gmail Disconnected Error Detected! Triggering parent handler");
        handleGmailError("GMAIL_DISCONNECTED", e.message);
        return;
      }

      setError(e.message || "Failed to save draft. Please try again.");
    }
    finally { setSaving(false); }
  };

  const fieldStyle = { width:"100%", border:"none", borderBottom:"1px solid #e0e0e0", outline:"none", fontSize:14, padding:"10px 0", color:"#202124", background:"transparent" };
  const w = wide ? "min(860px, 90vw)" : 500;

  return (
    <div style={{ position:"fixed", bottom:0, right:24, zIndex:1300, width:w, background:"#fff", borderRadius:"8px 8px 0 0", boxShadow:"0 8px 40px rgba(0,0,0,0.28)", display:"flex", flexDirection:"column", maxHeight: wide ? "85vh" : "80vh" }}>
      {/* Header */}
      <div style={{ background:"#c2e7ff", color:"#121212", padding:"10px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", borderRadius:"8px 8px 0 0", flexShrink:0 }}>
        <span style={{ fontSize:16, fontWeight:510 }}>New Message</span>
        <div style={{ display:"flex", gap:4 }}>
          <button onClick={()=>setWide(v=>!v)} style={{ background:"none", border:"none", cursor:"pointer", color:"#121212", padding:4, display:"flex" }} title={wide?"Compact":"Expand"}>
            {wide ? <FiMinimize2 size={15}/> : <FiMaximize2 size={15}/>}
          </button>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"#121212", padding:4, display:"flex" }}><FiX size={16}/></button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div style={{ background:"#ffebee", borderBottom:"1px solid #ef5350", padding:"12px 16px", display:"flex", alignItems:"center", gap:8, color:"#c62828", fontSize:13, flexShrink:0 }}>
          <FiAlertOctagon size={16} />
          <span>{error}</span>
          <button onClick={() => setError("")} style={{ marginLeft:"auto", background:"none", border:"none", cursor:"pointer", color:"#c62828", padding:0, fontSize:18 }}>×</button>
        </div>
      )}
      <div style={{ padding:"0 16px", flexShrink:0, borderBottom:"1px solid #e0e0e0" }}>
        {/* To + Cc Bcc toggle */}
        <div style={{ display:"flex", alignItems:"center" }}>
          <input value={to} onChange={e=>setTo(e.target.value)} placeholder="To" style={{ ...fieldStyle, flex:1 }} />
          <div style={{ display:"flex", gap:8, marginLeft:8, flexShrink:0 }}>
            {!showCc && <button onClick={()=>setShowCc(true)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:12, color:"#5f6368", fontWeight:500 }}>Cc</button>}
            {!showBcc && <button onClick={()=>setShowBcc(true)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:12, color:"#5f6368", fontWeight:500 }}>Bcc</button>}
          </div>
        </div>
        {showCc && <input value={cc} onChange={e=>setCc(e.target.value)} placeholder="Cc" style={fieldStyle} />}
        {showBcc && <input value={bcc} onChange={e=>setBcc(e.target.value)} placeholder="Bcc" style={fieldStyle} />}
        <input value={subject} onChange={e=>setSubject(e.target.value)} placeholder="Subject" style={fieldStyle} />
      </div>

      {/* Body */}
      <textarea value={body} onChange={e=>setBody(e.target.value)} placeholder="Compose email" style={{ flex:1, border:"none", outline:"none", padding:"12px 16px", fontSize:14, resize:"none", color:"#202124", fontFamily:"inherit", minHeight: wide ? 320 : 200 }} />

      {/* Footer */}
      <div style={{ padding:"10px 16px", borderTop:"1px solid #e0e0e0", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <button onClick={handleSend} disabled={!to||!subject||sending}
            style={{ background:(!to||!subject)?"#e0e0e0":"#1a73e8", color:"#fff", border:"none", borderRadius:4, padding:"8px 20px", fontWeight:600, fontSize:14, cursor:(!to||!subject)?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6 }}>
            {sending ? <CircularProgress size={13} style={{color:"#fff"}}/> : null}
            {sending ? "Sending…" : "Send"}
          </button>
          <button onClick={handleSaveDraft} disabled={saving}
            style={{ background:"none", border:"1px solid #dadce0", borderRadius:4, padding:"7px 14px", fontWeight:500, fontSize:13, cursor:"pointer", color:"#1a73e8" }}>
            {saving ? "Saving…" : "Save Draft"}
          </button>
        </div>
        <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"#5f6368", padding:4 }}><FiTrash2 size={17}/></button>
      </div>
    </div>
  );
}

export default function SalesInbox() {
  const [selectedFolder, setSelectedFolder] = useState("inbox");
  const [search, setSearch] = useState("");
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [openEmail, setOpenEmail] = useState(null);   // full-view email
  const [hoveredRow, setHoveredRow] = useState(null);
  const [senderCardId, setSenderCardId] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTo, setComposeTo] = useState("");
  const [sending, setSending] = useState(false);

  const normalise = (msg) => ({
    id:      msg._id || msg.id || Math.random().toString(36),
    sender:  msg.from || "",
    to:      msg.to || "",
    subject: msg.subject || "(no subject)",
    preview: msg.snippet || msg.body || "",
    date:    msg.date || new Date().toISOString(),
    unread:  !msg.isRead,
    starred: !!msg.isStarred,
    draftId: msg._id || null,
  });

  // Sort newest first
  const sortByDate = (arr) => [...arr].sort((a, b) => new Date(b.date) - new Date(a.date));

  const fetchEmails = useCallback(async (folder, pg = 1) => {
    if (!FOLDER_API_MAP[folder]) { setEmails([]); setHasMore(false); return; }
    pg === 1 ? setLoading(true) : setLoadingMore(true);
    try {
      const res = await apiRequest("get", `/gmail-url/mail/${FOLDER_API_MAP[folder]}?page=${pg}&limit=20`);
      const raw = res?.data?.emails || res?.emails || [];
      const normalised = raw.map(normalise);
      if (pg === 1) {
        setEmails(sortByDate(normalised));
      } else {
        setEmails(prev => sortByDate([...prev, ...normalised]));
      }
      // hasMore from API response
      const more = res?.data?.hasMore ?? res?.hasMore ?? (raw.length === 20);
      setHasMore(more);
      setPage(pg);
    } catch (err) { console.error("Fetch error:", err); if (pg === 1) setEmails([]); }
    finally { pg === 1 ? setLoading(false) : setLoadingMore(false); }
  }, []);

  useEffect(() => { fetchEmails(selectedFolder, 1); setOpenEmail(null); setPage(1); setHasMore(false); }, [selectedFolder]);

  const filteredEmails = emails.filter((e) => {
    const q = search.toLowerCase();
    return e.subject.toLowerCase().includes(q) || e.sender.toLowerCase().includes(q) || e.preview.toLowerCase().includes(q);
  });

  // Mark a single email read or unread via API, with optimistic update + rollback
  const handleReadStatus = useCallback(async (ev, emailId, isRead) => {
    if (ev) ev.stopPropagation();
    // Optimistic update in list
    setEmails(prev => prev.map(m => m.id === emailId ? { ...m, unread: !isRead } : m));
    // Keep openEmail in sync
    setOpenEmail(prev => prev?.id === emailId ? { ...prev, unread: !isRead } : prev);
    try {
      await apiRequest("patch", "/gmail-url/mail/read-status", { messageId: emailId, isRead });
    } catch (err) {
      console.error("Read status update failed:", err);
      // Rollback
      setEmails(prev => prev.map(m => m.id === emailId ? { ...m, unread: isRead } : m));
      setOpenEmail(prev => prev?.id === emailId ? { ...prev, unread: isRead } : prev);
    }
  }, []);

  // Toggle star via API, with optimistic update + rollback
  const handleStar = useCallback(async (ev, emailId, currentlyStarred) => {
    if (ev) ev.stopPropagation();
    const newStarred = !currentlyStarred;
    setEmails(prev => prev.map(m => m.id === emailId ? { ...m, starred: newStarred } : m));
    setOpenEmail(prev => prev?.id === emailId ? { ...prev, starred: newStarred } : prev);
    try {
      await apiRequest("patch", "/gmail-url/mail/star", { messageId: emailId });
      // If in starred folder and we just unstarred, remove from list
      if (currentlyStarred && selectedFolder === "starred") {
        setEmails(prev => prev.filter(m => m.id !== emailId));
        if (openEmail?.id === emailId) setOpenEmail(null);
      }
    } catch (err) {
      console.error("Star toggle failed:", err);
      setEmails(prev => prev.map(m => m.id === emailId ? { ...m, starred: currentlyStarred } : m));
      setOpenEmail(prev => prev?.id === emailId ? { ...prev, starred: currentlyStarred } : prev);
    }
  }, [selectedFolder, openEmail]);

  // Fetch full email body when opening an email
  const [emailBodyLoading, setEmailBodyLoading] = useState(false);
  const openEmailWithBody = useCallback(async (email) => {
    setOpenEmail(email);
    if (email.unread) handleReadStatus(null, email.id, true);
    setEmailBodyLoading(true);
    try {
      const res = await apiRequest("get", `/gmail-url/email/${email.id}`);
      const full = res?.data || res;
      if (full) {
        setOpenEmail(prev => prev?.id === email.id ? {
          ...prev,
          fullBody: full.body || null,
          unread: !full.isRead,
          starred: !!full.isStarred,
        } : prev);
      }
    } catch (err) {
      console.error("Full email fetch failed:", err);
    } finally {
      setEmailBodyLoading(false);
    }
  }, [handleReadStatus]);

  const handleDelete = async (ev, emailId) => {
    ev.stopPropagation();
    setEmails(prev => prev.filter(m => m.id !== emailId));
    if (openEmail?.id === emailId) setOpenEmail(null);
    try {
      await apiRequest("patch", `/gmail-url/mail/trash`, { messageId: emailId });
    } catch (err) {
      console.error("Delete failed:", err);
      fetchEmails(selectedFolder, 1);
    }
  };

  const handleSendDraft = async (draftId) => {
    if (!draftId) return;
    setSending(true);
    try {
      await apiRequest("post", "/gmail-url/send-draft-email", { draftId });
      setOpenEmail(null);
      fetchEmails("draft", 1);
    } catch (err) { console.error("Send draft failed:", err); }
    finally { setSending(false); }
  };

  const openCompose = (to = "") => { setComposeTo(to); setComposeOpen(true); };

  return (
    <>
      <div style={{ display:"flex", height:"calc(100vh - 16px)", background:"#f6f8fc", overflow:"hidden", fontFamily:"'Google Sans',Roboto,sans-serif" }}>

      {/* Sidebar */}
      <div style={{ width:220, background:"#f6f8fc", paddingTop:8, display:"flex", flexDirection:"column", flexShrink:0 }}>
        <div style={{ padding:"8px 16px 16px" }}>
          <button onClick={()=>openCompose()} style={{ background:"#c2e7ff", color:"#001d35", border:"none", borderRadius:16, fontWeight:500, fontSize:14, padding:"16px 24px", cursor:"pointer", display:"flex", alignItems:"center", gap:10, boxShadow:"0 1px 3px rgba(0,0,0,0.12)", transition:"box-shadow 0.2s" }}
            onMouseEnter={e=>e.currentTarget.style.boxShadow="0 2px 8px rgba(0,0,0,0.2)"} onMouseLeave={e=>e.currentTarget.style.boxShadow="0 1px 3px rgba(0,0,0,0.12)"}>
            <FiPlus size={20}/> Compose
          </button>
        </div>
        <nav>
          {folders.map((f) => {
            const active = selectedFolder === f.value;
            return (
              <button key={f.value} onClick={()=>setSelectedFolder(f.value)}
                style={{ width:"100%", display:"flex", alignItems:"center", gap:14, padding:"8px 16px 8px 26px", border:"none", cursor:"pointer", borderRadius:"0 20px 20px 0", marginBottom:2, background:active?"#d3e3fd":"transparent", color:active?"#041e49":"#444746", fontWeight:active?700:400, fontSize:14, transition:"background 0.15s" }}
                onMouseEnter={e=>{ if(!active) e.currentTarget.style.background="#e8eaed"; }}
                onMouseLeave={e=>{ if(!active) e.currentTarget.style.background="transparent"; }}>
                <span style={{ fontSize:18, display:"flex" }}>{f.icon}</span>
                {f.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Main */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", background:"#fff", borderRadius:"16px 0 0 16px", overflow:"hidden", margin:"8px 0" }}>
        {/* Search */}
        <div style={{ padding:"12px 16px 8px", borderBottom:"1px solid #e0e0e0", flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"center", background:"#eaf1fb", borderRadius:24, padding:"8px 16px", maxWidth:680, gap:8 }}>
            <FiSearch style={{ color:"#5f6368", fontSize:18, flexShrink:0 }}/>
            <input placeholder="Search mail" value={search} onChange={e=>setSearch(e.target.value)}
              style={{ border:"none", background:"transparent", outline:"none", fontSize:16, color:"#202124", width:"100%" }}/>
          </div>
        </div>

        {/* Content: list OR full email view */}
        {openEmail ? (
          /* Full email view — like Gmail's full view */
          <div style={{ flex:1, overflowY:"auto", padding:"24px 40px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
              <button onClick={()=>setOpenEmail(null)} style={{ background:"none", border:"none", cursor:"pointer", color:"#5f6368", padding:"6px 10px", borderRadius:4, fontSize:13, display:"flex", alignItems:"center", gap:6 }}
                onMouseEnter={e=>e.currentTarget.style.background="#f1f3f4"} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                ← Back
              </button>
              <button onClick={e=>handleDelete(e, openEmail.id)} style={{ background:"none", border:"none", cursor:"pointer", color:"#5f6368", padding:6, borderRadius:4, display:"flex" }}
                onMouseEnter={e=>e.currentTarget.style.color="#d93025"} onMouseLeave={e=>e.currentTarget.style.color="#5f6368"} title="Delete">
                <FiTrash2 size={17}/>
              </button>
              <button onClick={e=>handleReadStatus(e, openEmail.id, openEmail.unread)}
                title={openEmail.unread ? "Mark as read" : "Mark as unread"}
                style={{ background:"none", border:"none", cursor:"pointer", color:"#5f6368", padding:6, borderRadius:4, display:"flex", alignItems:"center", gap:5, fontSize:13 }}
                onMouseEnter={e=>e.currentTarget.style.color="#1a73e8"} onMouseLeave={e=>e.currentTarget.style.color="#5f6368"}>
                {openEmail.unread ? <FiEye size={16}/> : <FiEyeOff size={16}/>}
                {openEmail.unread ? "Mark as read" : "Mark as unread"}
              </button>
              <button onClick={e=>handleStar(e, openEmail.id, openEmail.starred)}
                title={openEmail.starred ? "Unstar" : "Star"}
                style={{ background:"none", border:"none", cursor:"pointer", padding:6, borderRadius:4, display:"flex", alignItems:"center", gap:5, fontSize:13, color: openEmail.starred ? "#f59e0b" : "#5f6368" }}
                onMouseEnter={e=>e.currentTarget.style.color="#f59e0b"} onMouseLeave={e=>e.currentTarget.style.color=openEmail.starred?"#f59e0b":"#5f6368"}>
                <FiStar size={16} fill={openEmail.starred ? "#f59e0b" : "none"}/>
                {openEmail.starred ? "Starred" : "Star"}
              </button>
            </div>
            <h2 style={{ fontSize:24, fontWeight:400, color:"#202124", margin:"0 0 20px" }}>{openEmail.subject}</h2>
            <div style={{ display:"flex", alignItems:"flex-start", gap:14, marginBottom:20 }}>
              <div style={{ width:40, height:40, borderRadius:"50%", background:avatarColor(extractName(openEmail.sender)), display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:16, fontWeight:600, flexShrink:0 }}>
                {extractName(openEmail.sender).charAt(0).toUpperCase()}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                  <div style={{ fontSize:14, fontWeight:600, color:"#202124", minWidth:0 }}>
                    {extractName(openEmail.sender)}
                    <span style={{ fontWeight:400, color:"#5f6368", marginLeft:6, fontSize:13 }}>&lt;{extractEmail(openEmail.sender)}&gt;</span>
                  </div>
                  <div style={{ fontSize:13, color:"#5f6368", whiteSpace:"nowrap", flexShrink:0 }}>
                    {new Date(openEmail.date).toLocaleString("en-US",{weekday:"short",month:"short",day:"numeric",year:"numeric",hour:"numeric",minute:"2-digit",hour12:true})}
                  </div>
                </div>
                {openEmail.to && (
                  <div style={{ fontSize:12, color:"#5f6368" }}>
                    to {openEmail.to.length > 28 ? openEmail.to.substring(0, 10) + "…" + openEmail.to.substring(openEmail.to.indexOf("@")) : openEmail.to}
                  </div>
                )}
              </div>
            </div>
            <Divider style={{ marginBottom:24 }}/>
            {emailBodyLoading ? (
              <div style={{ display:"flex", justifyContent:"center", padding:"40px 0" }}><CircularProgress size={24} style={{color:"#1a73e8"}}/></div>
            ) : openEmail.fullBody ? (
              <iframe
                srcDoc={openEmail.fullBody}
                sandbox="allow-same-origin"
                style={{ width:"100%", border:"none", minHeight:400, display:"block" }}
                title="Email body"
                onLoad={e => { try { e.target.style.height = e.target.contentDocument.body.scrollHeight + 32 + "px"; } catch(_){} }}
              />
            ) : (
              <div style={{ fontSize:14, lineHeight:1.8, color:"#202124", whiteSpace:"pre-wrap" }}>{openEmail.preview}</div>
            )}
            {selectedFolder === "draft" && openEmail.draftId && (
              <div style={{ marginTop:28 }}>
                <button disabled={sending} onClick={()=>handleSendDraft(openEmail.draftId)}
                  style={{ background:"#1a73e8", color:"#fff", border:"none", borderRadius:4, padding:"10px 24px", fontWeight:500, fontSize:14, cursor:sending?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:8 }}>
                  {sending?<CircularProgress size={13} style={{color:"#fff"}}/>:<FiSend size={14}/>}
                  {sending?"Sending…":"Send Draft"}
                </button>
              </div>
            )}
            <div style={{ display:"flex", gap:12, marginTop:32 }}>
              <button onClick={()=>openCompose(extractEmail(openEmail.sender))} style={{ border:"1px solid #dadce0", background:"#fff", borderRadius:20, padding:"8px 20px", fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", gap:6, color:"#202124" }}
                onMouseEnter={e=>e.currentTarget.style.background="#f1f3f4"} onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                ↩ Reply
              </button>
              <button onClick={()=>openCompose("")} style={{ border:"1px solid #dadce0", background:"#fff", borderRadius:20, padding:"8px 20px", fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", gap:6, color:"#202124" }}
                onMouseEnter={e=>e.currentTarget.style.background="#f1f3f4"} onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                ↪ Forward
              </button>
            </div>
          </div>
        ) : (
          /* Email list */
          <div style={{ flex:1, overflowY:"auto" }}>
            {loading ? (
              <div style={{ display:"flex", justifyContent:"center", alignItems:"center", height:200 }}><CircularProgress size={28} style={{color:"#1a73e8"}}/></div>
            ) : filteredEmails.length === 0 ? (
              <div style={{ display:"flex", justifyContent:"center", alignItems:"center", height:200, color:"#5f6368", fontSize:14 }}>No emails found, Go to setting under permission to connect your Gmail</div>
            ) : filteredEmails.map((email) => {
              const isHovered = hoveredRow === email.id;
              const isUnread = email.unread;
              const senderName = extractName(email.sender);
              const senderEmail = extractEmail(email.sender);
              const color = avatarColor(senderName);
              return (
                <div key={email.id} onClick={()=>openEmailWithBody(email)}
                  onMouseEnter={()=>setHoveredRow(email.id)}
                  onMouseLeave={()=>{ setHoveredRow(null); setSenderCardId(null); }}
                  style={{ display:"flex", alignItems:"center", padding:"0 16px", height:52, background:isHovered?"#f2f6fc":isUnread?"#f2f6fc":"#fff", borderBottom:"1px solid #f1f3f4", cursor:"pointer", transition:"background 0.1s", position:"relative" }}>
                  {/* Avatar */}
                  <div style={{ width:36, height:36, borderRadius:"50%", background:color, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:15, fontWeight:600, flexShrink:0, marginRight:12 }}>
                    {senderName.charAt(0).toUpperCase()}
                  </div>
                  {/* Sender */}
                  <div style={{ width:160, flexShrink:0, position:"relative", marginRight:16 }}
                    onMouseEnter={e=>{ e.stopPropagation(); setSenderCardId(email.id); }}
                    onMouseLeave={()=>setSenderCardId(null)}>
                    <span style={{ fontSize:14, fontWeight:isUnread?700:500, color:"#202124", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", display:"block" }}>{senderName}</span>
                    {senderCardId === email.id && <SenderCard raw={email.sender} onSendMail={()=>{ setSenderCardId(null); openCompose(senderEmail); }}/>}
                  </div>
                  {/* Subject + preview */}
                  <div style={{ flex:1, display:"flex", alignItems:"center", overflow:"hidden", marginRight:12 }}>
                    <span style={{ fontSize:14, fontWeight:isUnread?700:400, color:"#202124", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", flexShrink:0, maxWidth:"40%" }}>{email.subject}</span>
                    <span style={{ fontSize:14, color:"#5f6368", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", marginLeft:6 }}>— {email.preview}</span>
                  </div>
                  {/* Date + actions */}
                  <div style={{ display:"flex", alignItems:"center", gap:4, flexShrink:0 }}>
                    {isHovered && (
                      <>
                        <button title={isUnread ? "Mark as read" : "Mark as unread"} onClick={e=>handleReadStatus(e, email.id, isUnread)}
                          style={{ background:"none", border:"none", cursor:"pointer", color:"#5f6368", padding:"4px 6px", borderRadius:4, display:"flex" }}
                          onMouseEnter={e=>e.currentTarget.style.color="#1a73e8"} onMouseLeave={e=>e.currentTarget.style.color="#5f6368"}>
                          {isUnread ? <FiEye size={15}/> : <FiEyeOff size={15}/>}
                        </button>
                        <button title="Delete" onClick={e=>handleDelete(e, email.id)}
                          style={{ background:"none", border:"none", cursor:"pointer", color:"#5f6368", padding:"4px 6px", borderRadius:4, display:"flex" }}
                          onMouseEnter={e=>e.currentTarget.style.color="#d93025"} onMouseLeave={e=>e.currentTarget.style.color="#5f6368"}>
                          <FiTrash2 size={15}/>
                        </button>
                      </>
                    )}
                    {/* Star — always visible */}
                    <button title={email.starred ? "Unstar" : "Star"} onClick={e=>handleStar(e, email.id, email.starred)}
                      style={{ background:"none", border:"none", cursor:"pointer", padding:"4px 6px", borderRadius:4, display:"flex", color: email.starred ? "#f59e0b" : "#9ca3af", transition:"color 0.15s" }}
                      onMouseEnter={e=>e.currentTarget.style.color="#f59e0b"}
                      onMouseLeave={e=>e.currentTarget.style.color=e.currentTarget.dataset.starred==="true"?"#f59e0b":"#9ca3af"}
                      data-starred={email.starred}>
                      <FiStar size={15} fill={email.starred ? "#f59e0b" : "none"} stroke={email.starred ? "#f59e0b" : "currentColor"}/>
                    </button>
                    <span style={{ fontSize:12, color:"#5f6368", whiteSpace:"nowrap", minWidth:48, textAlign:"right" }}>{formatDate(email.date)}</span>
                  </div>
                </div>
              );
            })}
            {/* Load more */}
            {!loading && hasMore && (
              <div style={{ padding:"16px", display:"flex", justifyContent:"center", borderTop:"1px solid #f1f3f4" }}>
                <button onClick={()=>fetchEmails(selectedFolder, page + 1)} disabled={loadingMore}
                  style={{ background:"#fff", border:"1px solid #dadce0", borderRadius:20, padding:"8px 24px", fontSize:13, fontWeight:500, cursor:loadingMore?"not-allowed":"pointer", color:"#1a73e8", display:"flex", alignItems:"center", gap:6 }}
                  onMouseEnter={e=>{ if(!loadingMore) e.currentTarget.style.background="#f1f3f4"; }}
                  onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                  {loadingMore ? <><CircularProgress size={12} style={{color:"#1a73e8"}}/>&nbsp;Loading...</> : "Load more"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Compose window */}
      {composeOpen && (
        <ComposeWindow
          initialTo={composeTo}
          onClose={()=>{ setComposeOpen(false); setComposeTo(""); }}
          onSent={()=>{ if(selectedFolder==="sent") fetchEmails("sent", 1); }}
          onDrafted={()=>{ if(selectedFolder==="draft") fetchEmails("draft", 1); }}
          onGmailDisconnected={() => {
            setComposeOpen(false);
            // Global badge will show automatically via socket event
            // No need to manually manage state here
          }}
        />
      )}
      </div>
    </>
  );
}
